import React, { useRef } from 'react'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import { banks } from 'services/api/dropdown'
import { postBankCompanyDetails } from 'services/api/company-details'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { Formik, Field, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'

const EditBankDetailsModal = ({
  showModalBankDetails,
  handleCloseModalBankDetails,
  companyDetails,
}) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()
  const bankLists = useQuery('bankLists', banks)

  const initialValues = {
    bank_id: companyDetails?.data?.bank_id,
    company_bank_id: companyDetails?.data?.company_bank_id,
    ea_number: companyDetails?.data?.ea_number,
    bank_acc_number: companyDetails?.data?.bank_acc_number,
    epf_acc_number: companyDetails?.data?.epf_acc_number,
    socso_acc_number: companyDetails?.data?.socso_acc_number,
    skbbk_enabled: companyDetails?.data?.skbbk_enabled ?? 0,
    lhdn_acc_number: companyDetails?.data?.lhdn_acc_number,
    zakat_acc_number: companyDetails?.data?.zakat_acc_number,
    hrdf_status: companyDetails?.data?.hrdf_status,
    hrdf_rate: companyDetails?.data?.hrdf_rate,
    hrdf_acc_number: companyDetails?.data?.hrdf_acc_number,
  }

  const validationSchema = Yup.object().shape({
    bank_id: Yup.number().required('Bank name is required'),
    company_bank_id: Yup.string()
      .nullable()
      .matches(/^[a-zA-Z0-9]+$/, 'Only alphabets and numbers are allowed for this field'),
    ea_number: Yup.string()
      .nullable()
      .required('Company E-number is required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field'),
    bank_acc_number: Yup.string()
      .nullable()
      .required('Bank account no. is required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field'),
    epf_acc_number: Yup.string()
      .nullable()
      // .required('EPF account no.  is required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field'),
    socso_acc_number: Yup.string()
      .nullable()
      // .required('SOCSO account no. is required')
      .matches(/^[a-zA-Z0-9-]+$/, 'Only alphabets and numbers are allowed for this field'),
    skbbk_enabled: Yup.number().nullable().required('SKBBK contribution status is required'),
    lhdn_acc_number: Yup.string()
      .nullable()
      .required('LHDN account no. is required')
      .matches(/^[a-zA-Z0-9]+$/, 'Only alphabets and numbers are allowed for this field'),
    zakat_acc_number: Yup.string()
      .nullable()
      .matches(/^[a-zA-Z0-9-]+$/, 'Only alphabets and numbers are allowed for this field'),
    hrdf_status: Yup.string().required('HRDF contribution status is required'),
    hrdf_rate: Yup.string().when('hrdf_status', {
      is: '1',
      then: Yup.string().nullable().required('HRDF contribution rate is required'),
      otherwise: Yup.string().nullable(),
    }),
    hrdf_acc_number: Yup.string().when('hrdf_status', {
      is: '1',
      then: Yup.string()
        .nullable()
        .matches(/^[a-zA-Z0-9]+$/, 'Only alphabets and numbers are allowed for this field')
        .required('HRDF account number is required'),
      otherwise: Yup.string().nullable(),
    }),
  })

  const mutation = useMutation(postBankCompanyDetails, {
    onSuccess: (data) => {
      handleCloseModalBankDetails()
      queryClient.invalidateQueries('companyDetails')
    },
    onError: (e) => {
      if (e.status === 422) {
        handleValidationErrors(e, formikRef)
      } else {
        console.log(e.data.message)
      }
    },
  })

  const handleOnSubmit = (values) => {
    const normalizedValues = {
      ...values,
      skbbk_deduct_from_salary: values.skbbk_enabled === 1 ? 1 : 0,
    }
    const { hrdf_status, hrdf_acc_number, ...rest } = normalizedValues
    let newValues
    // if (values.socso_acc_number === '' || values.socso_acc_number === null) {
    //   newValues.socso_acc_number = ''
    // }

    // if (values.epf_acc_number === '' || values.epf_acc_number === null) {
    //   newValues.epf_acc_number = ''
    // }
    if (hrdf_status === 0) {
      newValues = { ...rest, hrdf_status }
    } else {
      newValues = normalizedValues
    }
    console.log('payload', newValues)
    mutation.mutate(newValues)
  }

  return (
    <Formik
      validationSchema={validationSchema}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleOnSubmit}
      innerRef={formikRef}
    >
      {({ values, errors, touched, handleChange, setFieldValue, handleSubmit, handleBlur }) => (
        <AdxFormModal
          title="Bank and statutory body details"
          subtitle=""
          open={showModalBankDetails}
          onClose={handleCloseModalBankDetails}
          handleClose={handleCloseModalBankDetails}
          enableFooter
          actionText="Save"
          handleAction={handleSubmit}
        >
          <Form>
            <Box
              minHeight={690}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxDropdown}
                      name="bank_id"
                      label="Bank name"
                      value={values?.bank_id}
                      onChange={handleChange}
                      isErrorShown={errors.bank_id && touched.bank_id}
                      errorMessage={<ErrorMessage name="bank_id" component={AdxErrorMessage} />}
                    >
                      {bankLists?.data?.data?.map((item) => (
                        <AdxMenuItem value={item.id} selectedvalue={values?.bank_id}>
                          {item.name}
                        </AdxMenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxInput}
                      name="company_bank_id"
                      label="Maybank Corporate ID"
                      size="standard"
                      placeholder="Maybank Corporate ID (e.g., MYJOHNSON)"
                      value={values?.company_bank_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="company_bank_id" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.company_bank_id && touched.company_bank_id}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxInput}
                      name="ea_number"
                      label="Company E-number"
                      size="standard"
                      placeholder="Company E-number"
                      value={values?.ea_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={<ErrorMessage name="ea_number" component={AdxErrorMessage} />}
                      isErrorShown={errors.ea_number && touched.ea_number}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxInput}
                      name="bank_acc_number"
                      label="Bank account no."
                      size="standard"
                      placeholder="Bank account no."
                      value={values?.bank_acc_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="bank_acc_number" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.bank_acc_number && touched.bank_acc_number}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxInput}
                      name="epf_acc_number"
                      label="EPF account no."
                      size="standard"
                      placeholder="EPF account no."
                      value={values?.epf_acc_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="epf_acc_number" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.epf_acc_number && touched.epf_acc_number}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxInput}
                      name="socso_acc_number"
                      label="SOCSO account no."
                      size="standard"
                      placeholder="SOCSO account no."
                      value={values?.socso_acc_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="socso_acc_number" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.socso_acc_number && touched.socso_acc_number}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxDropdown}
                      name="skbbk_enabled"
                      label="SKBBK contribution status"
                      value={values?.skbbk_enabled}
                      onChange={handleChange}
                      isErrorShown={errors.skbbk_enabled && touched.skbbk_enabled}
                      errorMessage={
                        <ErrorMessage name="skbbk_enabled" component={AdxErrorMessage} />
                      }
                    >
                      {[
                        { id: 1, name: 'Yes' },
                        { id: 0, name: 'No' },
                      ]?.map((item) => (
                        <AdxMenuItem value={item.id} selectedvalue={values?.skbbk_enabled}>
                          {item.name}
                        </AdxMenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxInput}
                      name="lhdn_acc_number"
                      label="LHDN account no."
                      size="standard"
                      placeholder="LHDN account no."
                      value={values?.lhdn_acc_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="lhdn_acc_number" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.lhdn_acc_number && touched.lhdn_acc_number}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxInput}
                      name="zakat_acc_number"
                      label="Zakat account no."
                      size="standard"
                      placeholder="Zakat account no."
                      value={values?.zakat_acc_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="zakat_acc_number" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.zakat_acc_number && touched.zakat_acc_number}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxDropdown}
                      name="hrdf_status"
                      label="HRDF contribution status"
                      value={values?.hrdf_status}
                      onChange={(event) => {
                        if (event.target.value === 0) {
                          if (values?.hrdf_rate) {
                            setFieldValue('hrdf_rate', values?.hrdf_rate)
                          } else {
                            setFieldValue('hrdf_rate', 0)
                          }
                        }
                        handleChange(event)
                      }}
                      isErrorShown={errors.hrdf_status && touched.hrdf_status}
                      errorMessage={<ErrorMessage name="hrdf_status" component={AdxErrorMessage} />}
                    >
                      {[
                        { id: 1, name: 'Yes' },
                        { id: 0, name: 'No' },
                      ]?.map((item) => (
                        <AdxMenuItem value={item.id} selectedvalue={values?.hrdf_status}>
                          {item.name}
                        </AdxMenuItem>
                      ))}
                    </Field>
                  </Grid>
                  {values?.hrdf_status === 1 && (
                    <Grid item xs={12} lg={12}>
                      <Field
                        as={AdxDropdown}
                        name="hrdf_rate"
                        label="HRDF contribution rate"
                        value={values?.hrdf_rate}
                        onChange={handleChange}
                        isErrorShown={errors.hrdf_rate && touched.hrdf_rate}
                        errorMessage={<ErrorMessage name="hrdf_rate" component={AdxErrorMessage} />}
                      >
                        {[
                          { id: 0, value: '0.5 %' },
                          { id: 1, value: '1 %' },
                        ]?.map((item) => (
                          <AdxMenuItem value={item.id} selectedvalue={values?.hrdf_rate}>
                            {item.value}
                          </AdxMenuItem>
                        ))}
                      </Field>
                    </Grid>
                  )}
                  <Grid item xs={12} lg={12}>
                    <Field
                      as={AdxInput}
                      name="hrdf_acc_number"
                      label="HRDF  account no."
                      size="standard"
                      placeholder="HRDF  account no."
                      value={values?.hrdf_acc_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        <ErrorMessage name="hrdf_acc_number" component={AdxErrorMessage} />
                      }
                      isErrorShown={errors.hrdf_acc_number && touched.hrdf_acc_number}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Form>
        </AdxFormModal>
      )}
    </Formik>
  )
}

export default EditBankDetailsModal
